import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { useSearchParams } from 'react-router-dom';
import { getPV, createPV, deletePV, updatePVStatus, getChurchProfile, getExpenseCategories, getPVPendingCounts } from '../../services/api';
import { FiPlus, FiTrash2, FiX, FiCreditCard, FiCheck, FiPrinter } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { printPaymentVoucher, printPVList } from '../../utils/printReceipt';
import useAppRefresh from '../../hooks/useAppRefresh';

const statusPill = { pending: 'pill-yellow', approved: 'pill-blue', paid: 'pill-green', rejected: 'pill-red' };

// Derive a display label that reflects the two-step approval state.
const displayStatus = (r) => {
  if (r.status === 'pending' && r.finance_approved_at && !r.admin_approved_at) return 'awaiting admin';
  if (r.status === 'pending') return 'awaiting finance';
  return r.status;
};

const PV = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // v1.9.49 — Tab: 'all' (default) or 'pending'. The PV Approval badge in
  // the top bar links here with ?tab=pending so the approver lands directly
  // on the filtered list.
  const [tab, setTab] = useState(searchParams.get('tab') === 'pending' ? 'pending' : 'all');
  const [pendingCount, setPendingCount] = useState(0);
  const [list, setList] = useState([]);
  const [profile, setProfile] = useState({});
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [statusFilter, setStatusFilter] = useState('');
  // v1.9.123 — tokenised Issued To search. Same split-match pattern as the
  // Cash Receipts filter: every whitespace token must appear somewhere in
  // Issued To / Description / Reason. Pure client-side over the loaded list.
  const [nameFilter, setNameFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), paid_to: '', description: '', amount: '', currency: '', payment_method: 'cash', expense_category_id: '' });
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  // v1.9.49 — When "Pending Approval" tab is active, always show pending PVs
  // regardless of the status dropdown (which is hidden in that mode). Also
  // widen the date range to "all time" so a pending PV dated last month
  // doesn't disappear because the operator's date filter defaults to today.
  const fetch = () => {
    if (tab === 'pending') {
      return getPV({ status: 'pending' }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
    return getPV({ from, to, status: statusFilter }).then(r => setList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useAppRefresh(fetch);

  useEffect(() => { fetch(); }, [from, to, statusFilter, tab]);
  useEffect(() => { getChurchProfile().then(r => setProfile(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { getExpenseCategories().then(r => setExpenseTypes(Array.isArray(r.data) ? r.data.filter(c => c.active) : [])).catch(() => {}); }, []);
  // v1.9.49 — pending count for the tab pill. Same endpoint the top-bar
  // badge uses so both numbers stay in sync. Refreshes on tab change so
  // the count updates the moment an approver clears a PV.
  useEffect(() => {
    getPVPendingCounts()
      .then(r => setPendingCount(parseInt(r.data?.total || r.data?.count || r.data?.pending || 0) || 0))
      .catch(() => {});
  }, [tab, list]);

  // v1.9.49 — keep the URL in sync with tab state so page-refresh preserves
  // the pending view and the browser back button behaves as expected.
  useEffect(() => {
    const cur = searchParams.get('tab');
    if (tab === 'pending' && cur !== 'pending') {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'pending');
      setSearchParams(next, { replace: true });
    } else if (tab === 'all' && cur === 'pending') {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [tab]); // eslint-disable-line

  // v1.9.87 — split totals by currency so the PV table mirrors the CR table's
  // two-currency ('Amount $' / 'Amount K') presentation.
  // v1.9.123 — tokenised Issued To / Description / Reason filter.
  const nameTokens = String(nameFilter || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filteredList = nameTokens.length === 0 ? list : list.filter(r => {
    const hay = [r.paid_to, r.description, r.expense_category_name]
      .filter(Boolean).join(' ').toLowerCase();
    return nameTokens.every(t => hay.includes(t));
  });
  const totalUsd = filteredList.reduce((s, r) => s + ((r.currency || cur1) === '$' ? parseFloat(r.amount) || 0 : 0), 0);
  const totalKwacha = filteredList.reduce((s, r) => s + ((r.currency || cur1) === 'K' ? parseFloat(r.amount) || 0 : 0), 0);
  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : ['$', 'K'];
  const cur1 = currencies[0] || '$';
  // Default the new-PV currency selector to the primary once profile loads.
  useEffect(() => { if (!form.currency && cur1) setForm(p => ({ ...p, currency: cur1 })); /* eslint-disable-next-line */ }, [cur1]);

  const handleAdd = async () => {
    setError('');
    if (!form.date || !form.paid_to || !form.amount) return setError('Date, paid to, and amount are required');
    setSaving(true);
    try {
      // Pre-compute the bucket on the client so the cashbook can credit/debit
      // the right drawer immediately. Backend recomputes the same thing as
      // a defence-in-depth (so curl callers don't bypass it).
      const sym = form.currency || cur1;
      const amt = parseFloat(form.amount) || 0;
      const m = (form.payment_method || 'cash').toLowerCase();
      const buckets = { usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 };
      if (amt > 0) {
        if (sym === '$') buckets[m === 'bank' ? 'usd_bank' : 'usd_cash'] = amt;
        else             buckets[m === 'bank' ? 'k_bank' : m === 'momo' ? 'k_momo' : 'k_cash'] = amt;
      }
      await createPV({ ...form, buckets });
      setShowAdd(false);
      setForm({ date: new Date().toISOString().slice(0, 10), paid_to: '', description: '', amount: '', currency: cur1, payment_method: 'cash', expense_category_id: '' });
      showToast('Payment voucher created — awaiting Finance approval');
      fetch();
    } catch (e) { setError(e.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const reprintVoucher = (r) => {
    printPaymentVoucher({
      pv_number: r.pv_number,
      date: r.date,
      paid_to: r.paid_to,
      description: r.description,
      amount: r.amount,
      currency: r.currency || cur1,
      status: r.status,
      created_by_name: r.created_by_name || '',
      expense_category_name: r.expense_category_name || '',
    }, profile);
  };

  const handleStatus = async (r, status) => {
    try {
      await updatePVStatus(r.id, { status });
      const label = status === 'finance_approved' ? 'Finance approved'
                  : status === 'approved'         ? 'Admin approved'
                  : status === 'paid'             ? 'Marked as paid'
                  : status === 'rejected'         ? 'Rejected'
                  : `Status: ${status}`;
      showToast(label);
      fetch();
    } catch (e) {
      showToast(e.response?.data?.error || 'Action failed', 'error');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.pv_number}?`)) return;
    await deletePV(r.id);
    showToast('Deleted', 'error');
    fetch();
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.pv')}</h1><p>{t('common.total')}: {totalUsd > 0 ? `$${totalUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '$0.00'} · {totalKwacha > 0 ? `K${totalKwacha.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : 'K0.00'}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isMobile && (
            <button className="btn btn-secondary" onClick={() => printPVList({ records: filteredList, profile, from, to, filterLabel: [statusFilter, nameFilter ? `Issued to: ${nameFilter}` : ''].filter(Boolean).join(' · ') })} disabled={filteredList.length === 0}>
              <FiPrinter /> {t('recordAttendance.print')}
            </button>
          )}
          {user?.role === 'cashier' && <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus /> {t('form.newPV')}</button>}
        </div>
      </div>

      {/* v1.9.49 — Tabs. "All PVs" is the default browsing view; "Pending
          Approval" jumps straight to approver mode. Top-bar PV Approval badge
          navigates here with ?tab=pending. */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
        <button
          onClick={() => setTab('all')}
          style={{
            background: 'none', border: 'none', padding: '10px 18px', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: tab === 'all' ? '#4f46e5' : '#6b7280',
            borderBottom: tab === 'all' ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: -1,
          }}
        >All PVs</button>
        <button
          onClick={() => setTab('pending')}
          style={{
            background: 'none', border: 'none', padding: '10px 18px', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            color: tab === 'pending' ? '#4f46e5' : '#6b7280',
            borderBottom: tab === 'pending' ? '2px solid #4f46e5' : '2px solid transparent',
            marginBottom: -1,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          Pending Approval
          {pendingCount > 0 && (
            <span style={{ background: '#dc2626', color: '#fff', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Date + status filters — only shown on "All PVs" tab. The Pending
          Approval tab always shows every pending PV regardless of date so the
          approver doesn't miss anything older than today. */}
      {tab === 'all' && (
        // v1.9.73 — same compact grid layout as Cash Book filters
        // (bypasses .filters-row so mobile CSS can't force column-stack)
        // + Clear button that resets date + status filters.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{t('table.from')}</span>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{t('table.to')}</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Status</span>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}>
              <option value="">{t('common.all')}</option>
              <option value="pending">{t('subscriptions.due')}</option>
              <option value="approved">{t('approve.approveBtn')}</option>
              <option value="paid">{t('subscriptions.paid')}</option>
              <option value="rejected">{t('approve.rejectBtn')}</option>
            </select>
          </label>
          {/* v1.9.123 — tokenised Issued To search (matches Issued To /
              Description / Reason). Same split-match as the CR filter. */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{t('table.issuedTo')}</span>
            <input type="search" value={nameFilter} onChange={e => setNameFilter(e.target.value)}
              placeholder={t('table.issuedTo') + '…'}
              style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
          {(from || to || statusFilter || nameFilter) && (
            <button
              onClick={() => { setFrom(''); setTo(''); setStatusFilter(''); setNameFilter(''); }}
              style={{ alignSelf: 'end', padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Clear all filters"
            >Clear</button>
          )}
        </div>
      )}

      <div className="table-card">
        <table>
          <thead>
            <tr><th>#</th><th>PV {t('finance.crNumber').replace('CR', 'PV')}</th><th>{t('common.date')}</th><th>{t('table.issuedTo')}</th><th>{t('pv.reasonType')}</th><th>{t('table.description')}</th><th>Paid From</th><th>Amount $</th><th>Amount K</th><th>{t('common.status')}</th><th>{t('common.actions')}</th></tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr><td colSpan={11}><div className="empty-state"><div className="empty-icon"><FiCreditCard /></div><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : filteredList.map((r, i) => (
              <tr key={r.id}>
                <td data-label="#">{i + 1}</td>
                <td data-label={`PV ${t('finance.crNumber').replace('CR', 'PV')}`}><strong>{r.pv_number}</strong></td>
                <td data-label={t('common.date')}>{r.date}</td>
                <td data-label={t('table.issuedTo')}>{r.paid_to}</td>
                <td data-label={t('pv.reasonType')}>{r.expense_category_name || '—'}</td>
                <td data-label={t('table.description')} style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '—'}</td>
                {(() => {
                  // v1.9.126 — "Paid From" derives from the 5 bucket columns on
                  // the PV row (usd_cash / usd_bank / k_cash / k_bank / k_momo).
                  // Whichever bucket holds a non-zero amount is where the money
                  // came out of — matches the Cash Book's own aggregation.
                  const bucket = ['usd_cash','usd_bank','k_cash','k_bank','k_momo'].find(k => parseFloat(r[k] || 0) > 0);
                  const label = { usd_cash: 'Cash', usd_bank: 'Bank', k_cash: 'Cash', k_bank: 'Bank', k_momo: 'Mobile' }[bucket] || '—';
                  const color = { Cash: { bg: '#fef3c7', fg: '#92400e' }, Bank: { bg: '#dbeafe', fg: '#1e40af' }, Mobile: { bg: '#dcfce7', fg: '#166534' } }[label] || { bg: '#f3f4f6', fg: '#6b7280' };
                  return (
                    <td data-label="Paid From">
                      {label === '—' ? <span style={{ color: '#9ca3af' }}>—</span>
                        : <span style={{ padding: '2px 8px', borderRadius: 10, background: color.bg, color: color.fg, fontSize: 11, fontWeight: 600 }}>{label}</span>}
                    </td>
                  );
                })()}
                {(() => {
                  const sym = r.currency || cur1;
                  const val = parseFloat(r.amount) || 0;
                  const isUsd = sym === '$';
                  return (
                    <>
                      <td data-label="Amount $"><strong style={{ color: isUsd && val > 0 ? '#dc2626' : '#9ca3af' }}>{isUsd && val > 0 ? `$${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</strong></td>
                      <td data-label="Amount K"><strong style={{ color: !isUsd && val > 0 ? '#dc2626' : '#9ca3af' }}>{!isUsd && val > 0 ? `K${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</strong></td>
                    </>
                  );
                })()}
                <td data-label={t('common.status')}><span className={`pill ${statusPill[r.status]}`}>{displayStatus(r)}</span></td>
                <td data-label={t('common.actions')}>
                  <div className="action-btns">
                    {r.status === 'pending' && !r.finance_approved_at && user?.role === 'finance' && (
                      <>
                        <button className="btn btn-sm btn-success" onClick={() => handleStatus(r, 'finance_approved')}><FiCheck /> Finance Approve</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleStatus(r, 'rejected')}>{t('approve.rejectBtn')}</button>
                      </>
                    )}
                    {r.status === 'pending' && r.finance_approved_at && user?.role === 'admin' && (
                      <>
                        <button className="btn btn-sm btn-success" onClick={() => handleStatus(r, 'approved')}><FiCheck /> Admin Approve</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleStatus(r, 'rejected')}>{t('approve.rejectBtn')}</button>
                      </>
                    )}
                    {/* v1.9.84 — Paid button removed. Admin approval now flips
                        status straight to 'paid', so the PV hits the cash book
                        the moment the Admin approves. */}
                    {/* v1.9.50 — hide print + delete on the Pending Approval
                        tab so approvers only see the actions relevant to
                        their decision. Both still available on "All PVs". */}
                    {tab === 'all' && (
                      <>
                        {!isMobile && <button className="btn-icon" title={t('recordAttendance.print')} onClick={() => reprintVoucher(r)}><FiPrinter /></button>}
                        <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {filteredList.length > 0 && (
            <tfoot>
              <tr style={{ background: '#f9fafb' }}>
                {/* v1.9.126 — 11-column table: #, PV, DATE, ISSUED, REASON,
                    DESC, PAID FROM, AMOUNT $, AMOUNT K, STATUS, ACTIONS. */}
                <td colSpan={7} style={{ padding: '10px 16px', fontWeight: 700, fontSize: 13 }}>Total</td>
                <td style={{ padding: '10px 16px', fontWeight: 700 }}>{totalUsd > 0 ? `$${totalUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>
                <td style={{ padding: '10px 16px', fontWeight: 700 }}>{totalKwacha > 0 ? `K${totalKwacha.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div><h3>New Payment Voucher</h3></div>
              <button className="btn-icon" onClick={() => setShowAdd(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {error && <div className="error-msg">{error}</div>}
              <div className="form-grid">
                <div className="form-group"><label>Date <span className="required">*</span></label><input type="date" value={form.date} onChange={set('date')} /></div>
                <div className="form-group">
                  <label>Amount <span className="required">*</span></label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select value={form.currency || cur1} onChange={set('currency')} style={{ flex: '0 0 80px' }}>
                      {currencies.map(sym => <option key={sym} value={sym}>{sym}</option>)}
                    </select>
                    <input type="number" step="0.01" value={form.amount} onChange={set('amount')} style={{ flex: 1 }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('cr.modeOfPayment')} <span className="required">*</span></label>
                  <select value={form.payment_method} onChange={set('payment_method')}>
                    <option value="cash">{t('finance.cash')}</option>
                    <option value="bank">{t('finance.bank')}</option>
                    {(form.currency || cur1) === 'K' && (
                      <option value="momo">{t('finance.mobile')}</option>
                    )}
                  </select>
                </div>
                <div className="form-group form-full"><label>Paid To <span className="required">*</span></label><input value={form.paid_to} onChange={set('paid_to')} placeholder="Payee name" /></div>
                <div className="form-group form-full">
                  <label>{t('pv.reasonType')}</label>
                  <select value={form.expense_category_id} onChange={set('expense_category_id')}>
                    <option value="">{t('form.selectType')}</option>
                    {expenseTypes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {expenseTypes.length === 0 && (
                    <small style={{ color: '#9ca3af', fontSize: 11 }}>{t('pv.noExpenseTypes')}</small>
                  )}
                </div>
                <div className="form-group form-full"><label>Description</label><textarea value={form.description} onChange={set('description')} rows={2} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default PV;
